import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './additional-discounts.api';

import type {
  CreateAdditionalDiscountRequest,
  AdditionalDiscountListQuery,
  UpdateAdditionalDiscountRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: AdditionalDiscountListQuery) => ['additional-discounts', 'list', query] as const,
  detail: (id: string) => ['additional-discounts', 'detail', id] as const,
};

export const useAdditionalDiscountsList = (query: AdditionalDiscountListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listAdditionalDiscounts(query),
    staleTime: 30_000,
  });

export const useCreateAdditionalDiscount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAdditionalDiscountRequest) => api.createAdditionalDiscount(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['additional-discounts', 'list'] });
      toast.success(`Added ${row.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add upsell'),
  });
};

export const useUpdateAdditionalDiscount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAdditionalDiscountRequest }) =>
      api.updateAdditionalDiscount(id, payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['additional-discounts', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(row.id) });
      toast.success('Upsell updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update upsell'),
  });
};

export const useDeleteAdditionalDiscount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAdditionalDiscount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-discounts', 'list'] });
      toast.success('Upsell deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete upsell'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
